"use client"

import { Box, Flex, Image, Link, Stack, Drawer, CloseButton, VStack, IconButton } from "@chakra-ui/react"
import { useColorModeValue } from "./ui/color-mode"
import { FiMenu } from "react-icons/fi"
import { useState } from "react"
import LoginButton from "./LoginButton"
import Profile from "./Profile"
import StockSearch from "./StockSearch"
import { useRecentStocks } from "../hooks/useRecentStocks"
import { SIGNATURE_GREEN } from "../theme/brandColors"

const Navbar = () => {
  const bg = useColorModeValue("white", "gray.900")
  const color = useColorModeValue("black", "white")
  const hoverBg = useColorModeValue("gray.200", "gray.700")
  const borderColor = useColorModeValue("gray.200", "gray.800")
  const inputBg = useColorModeValue("white", "gray.800")
  const placeholderColor = useColorModeValue("gray.500", "gray.400")
  const [isOpen, setIsOpen] = useState(false)
  const { addRecent } = useRecentStocks({ fetchOnMount: false })

  const onOpen = () => setIsOpen(true)
  const onClose = () => setIsOpen(false)
  const handleSuggestionSelected = async (suggestion) => {
    try {
      await addRecent?.(suggestion.ticker)
    } catch (err) {
      console.error("Failed to record recent stock", err)
    }
  }

  const navLinks = [
    { href: "/", label: "Home" },
    { href: "/about", label: "About" },
    { href: "/browse", label: "Browse" },
    { href: "/stock-baskets", label: "Stock Baskets" },
    { href: "/tutorials", label: "Tutorials" },
    { href: "/changelog", label: "Changelog" },
    { href: "/contact", label: "Contact" },
  ]

  const stockSearchInputProps = {
    size: "md",
    bg: inputBg,
    pl: 4,
    pr: "2.5rem",
    h: 10,
    fontSize: "sm",
    _placeholder: { color: placeholderColor },
  }

  return (
    <Box bg={bg} px={4} borderBottom="1px solid" borderColor={borderColor}>
      <Flex h={16} alignItems={"center"} gap={4} w="100%">
        <Link
          href="/"
          display="flex"
          alignItems="center"
          gap={2}
          fontWeight="bold"
          fontSize="lg"
          color={color}
          flexShrink={0}
          textDecoration="none"
          borderRadius="md"
          px={2}
          py={1}
          transition="background-color 0.2s ease, color 0.2s ease, transform 0.2s ease"
          _hover={{
            bg: hoverBg,
            color: SIGNATURE_GREEN,
            textDecoration: "none",
            transform: "translateY(-1px)",
          }}
        >
          <Image
            src="/dcf_teacher_logo512.jpg"
            alt=""
            boxSize="32px"
            objectFit="contain"
            flexShrink={0}
          />
          <Box as="span" whiteSpace="nowrap">
            DCF Teacher
          </Box>
        </Link>
        <Box
          flex="1"
          minW={0}
          display={{ base: "none", md: "block" }}
        >
          <StockSearch
            placeholder="Search stocks"
            onSuggestionSelected={handleSuggestionSelected}
            containerProps={{ w: "100%" }}
            inputProps={{
              ...stockSearchInputProps,
              borderRadius: "full",
            }}
          />
        </Box>

        <Flex alignItems={"center"} gap={3} ml="auto" flexShrink={0}>
          <Flex alignItems={"center"} display={{ base: "none", lg: "flex" }} gap={1}>
            {navLinks.map((link) => (
              <Link key={link.href} href={link.href} px={2} color={color} whiteSpace="nowrap">
                {link.label}
              </Link>
            ))}
          </Flex>

          <Flex alignItems={"center"} gap={2} display={{ base: "none", md: "flex" }}>
            <Profile />
            <LoginButton />
          </Flex>

          <IconButton
            aria-label="Open menu"
            onClick={onOpen}
            variant="ghost"
            size="sm"
            display={{ base: "flex", lg: "none" }}
            _hover={{
              bg: hoverBg
            }}
            transition="background-color 0.2s"
          >
            <FiMenu size={20} />
          </IconButton>
        </Flex>
      </Flex>

      <Drawer.Root
        open={isOpen}
        onOpenChange={(details) => setIsOpen(details.open)}
        placement="end"
        size="sm"
      >
        <Drawer.Backdrop />
        <Drawer.Positioner>
          <Drawer.Content bg={bg}>
            <Box p={4} borderBottomWidth="1px" display="flex" justifyContent="space-between" alignItems="center">
              <Box fontWeight="bold" fontSize="lg" color={color}>Menu</Box>
              <CloseButton
                onClick={onClose}
                _hover={{
                  bg: hoverBg
                }}
                transition="background-color 0.2s"
              />
            </Box>
            <Box p={4} borderBottomWidth="1px" display={{ base: "block", md: "none" }}>
              <StockSearch
                placeholder="Search stocks"
                onSuggestionSelected={handleSuggestionSelected}
                onSelectionComplete={onClose}
                containerProps={{ w: "100%" }}
                inputProps={{
                  ...stockSearchInputProps,
                  borderRadius: "md",
                }}
              />
            </Box>
            <Box p={4}>
              <VStack spacing={4} align="stretch">
                {navLinks.map((link) => (
                  <Link
                    key={link.href}
                    href={link.href}
                    py={2}
                    onClick={onClose}
                    fontSize="lg"
                    color={color}
                  >
                    {link.label}
                  </Link>
                ))}
                <Stack spacing={4} display={{ base: "flex", md: "none" }}>
                  <Box py={2}>
                    <Profile />
                  </Box>
                  <Box py={2}>
                    <LoginButton />
                  </Box>
                </Stack>
              </VStack>
            </Box>
          </Drawer.Content>
        </Drawer.Positioner>
      </Drawer.Root>
    </Box>
  )
}

export default Navbar
